/**
 * CefSharp (.NET Chromium) bots — e.g. Microsoft Outlook SafeLinks — and some
 * password-manager extensions reject promises with this string. Not app errors.
 * @see https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
 */
const CEFSHARP_BOT_REJECTION
  = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/

/** Stale JS chunks after a deploy — Nuxt reloads automatically via emitRouteChunkError. */
const CHUNK_LOAD_ERROR
  = /(?:Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk \d+ failed|not a valid JavaScript MIME type)/i

const NETWORK_FETCH_ERROR
  = /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)$/

/** App-level errors that are intentional sentinels, not bugs.
 * `AuthTokenUnavailableError` is thrown by `api/client.ts` when no Firebase
 * session is available; callers are expected to treat it as "skip the call". */
const APP_EXPECTED_ERROR_TYPES = new Set(['AuthTokenUnavailableError'])

const messageFromUnknown = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value
  if (value instanceof Error) return value.message
  return undefined
}

const isIgnoredExceptionMessage = (message: string): boolean =>
  CEFSHARP_BOT_REJECTION.test(message) || CHUNK_LOAD_ERROR.test(message)

export const isIgnoredThirdPartyException = (value: unknown): boolean => {
  const message = messageFromUnknown(value)
  return message !== undefined && isIgnoredExceptionMessage(message)
}

type PostHogCaptureEvent = {
  event?: string
  properties?: Record<string, unknown>
}

const exceptionMessageMatches = (value: unknown): boolean =>
  typeof value === 'string' && isIgnoredExceptionMessage(value)

const isAppExpectedException = (item: Record<string, unknown>): boolean =>
  typeof item.type === 'string' && APP_EXPECTED_ERROR_TYPES.has(item.type)

const isHandledException = (item: Record<string, unknown>): boolean => {
  const mechanism = item.mechanism
  if (typeof mechanism !== 'object' || mechanism === null) return false
  return (mechanism as { handled?: unknown }).handled === true
}

const isHandledNetworkFetchException = (item: Record<string, unknown>): boolean =>
  isHandledException(item)
  && typeof item.value === 'string'
  && NETWORK_FETCH_ERROR.test(item.value)

/** Drop third-party bot/extension noise before it reaches PostHog error tracking. */
export const shouldDropPostHogEvent = (event: PostHogCaptureEvent): boolean => {
  if (event.event !== '$exception') return false

  const props = event.properties ?? {}

  const values = props.$exception_values
  if (Array.isArray(values) && values.some(exceptionMessageMatches)) {
    return true
  }

  const list = props.$exception_list
  if (Array.isArray(list)) {
    for (const item of list) {
      if (typeof item === 'object' && item !== null && 'value' in item) {
        const exceptionItem = item as Record<string, unknown>
        if (
          isAppExpectedException(exceptionItem)
          || exceptionMessageMatches(exceptionItem.value)
          || isHandledNetworkFetchException(exceptionItem)
        ) {
          return true
        }
      }
    }
  }

  return false
}
